// src/utils/rawMaterialInventoryModel.js

// 📦 Raw Material Inventory Schema
export const RawMaterialInventorySchema = {
  _id: '',
  branch: '', // Branch ID or populated branch object
  rawMaterial: '', // Raw Material ID or populated raw material object
  currentStock: 0,
  unitPrice: 0,
  isActive: true,
  lastUpdated: null,
  createdAt: null,
  updatedAt: null
}

// 🛡️ Validation Rules
export const INVENTORY_VALIDATION_RULES = {
  branch: {
    required: true,
    message: 'Branch is required'
  },
  rawMaterial: {
    required: true,
    message: 'Raw material is required'
  },
  currentStock: {
    required: true,
    min: 0,
    max: 999999,
    message: 'Current stock must be a positive number between 0 and 999999'
  },
  unitPrice: {
    required: false,
    min: 0,
    max: 999999,
    message: 'Unit price must be a positive number'
  }
}

// 📊 Stock Status Enums
export const STOCK_STATUS = {
  NORMAL: 'normal',
  LOW_STOCK: 'low_stock',
  OUT_OF_STOCK: 'out_of_stock',
  OVER_STOCK: 'over_stock'
}

// 🎨 Status Display Labels
export const STOCK_STATUS_LABELS = {
  [STOCK_STATUS.NORMAL]: 'Normal',
  [STOCK_STATUS.LOW_STOCK]: 'Low Stock',
  [STOCK_STATUS.OUT_OF_STOCK]: 'Out of Stock',
  [STOCK_STATUS.OVER_STOCK]: 'Over Stock'
}

// 🎨 Status Colors
export const STOCK_STATUS_COLORS = {
  [STOCK_STATUS.NORMAL]: 'bg-blue-100 text-blue-800',
  [STOCK_STATUS.LOW_STOCK]: 'bg-yellow-100 text-yellow-800',
  [STOCK_STATUS.OUT_OF_STOCK]: 'bg-red-100 text-red-800',
  [STOCK_STATUS.OVER_STOCK]: 'bg-green-100 text-green-800'
}

// 🏭 Raw Material Inventory Model Class
export class RawMaterialInventoryModel {
  
  // Create a new inventory instance
  static create(data = {}) {
    return {
      ...RawMaterialInventorySchema,
      ...data,
      _id: data._id || `temp_${Date.now()}`,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString()
    }
  }
  
  // Validate inventory data
  static validate(inventoryData) {
    const errors = {}
    
    // Validate branch
    if (!inventoryData.branch || !inventoryData.branch.trim()) {
      errors.branch = INVENTORY_VALIDATION_RULES.branch.message
    }
    
    // Validate raw material
    if (!inventoryData.rawMaterial || !inventoryData.rawMaterial.trim()) {
      errors.rawMaterial = INVENTORY_VALIDATION_RULES.rawMaterial.message
    }
    
    // Validate current stock
    if (inventoryData.currentStock === undefined || inventoryData.currentStock === null || inventoryData.currentStock === '') {
      errors.currentStock = 'Current stock is required'
    } else {
      const stock = Number(inventoryData.currentStock)
      if (isNaN(stock)) {
        errors.currentStock = 'Current stock must be a valid number'
      } else if (stock < INVENTORY_VALIDATION_RULES.currentStock.min) {
        errors.currentStock = `Current stock must be at least ${INVENTORY_VALIDATION_RULES.currentStock.min}`
      } else if (stock > INVENTORY_VALIDATION_RULES.currentStock.max) {
        errors.currentStock = INVENTORY_VALIDATION_RULES.currentStock.message
      }
    }
    
    // Validate unit price (optional)
    if (inventoryData.unitPrice !== undefined && inventoryData.unitPrice !== null && inventoryData.unitPrice !== '') {
      const price = Number(inventoryData.unitPrice)
      if (isNaN(price) || price < 0) {
        errors.unitPrice = INVENTORY_VALIDATION_RULES.unitPrice.message
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Validate partial data for updates
  static validatePartial(inventoryData) {
    const errors = {}
    
    // Only validate fields that are provided
    if (inventoryData.currentStock !== undefined) {
      const stock = Number(inventoryData.currentStock)
      if (isNaN(stock) || stock < 0) {
        errors.currentStock = INVENTORY_VALIDATION_RULES.currentStock.message
      }
    }
    
    if (inventoryData.unitPrice !== undefined) {
      const price = Number(inventoryData.unitPrice)
      if (isNaN(price) || price < 0) {
        errors.unitPrice = INVENTORY_VALIDATION_RULES.unitPrice.message
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Determine stock status based on current stock and threshold
  static getStockStatus(currentStock, threshold) {
    if (currentStock === 0) {
      return STOCK_STATUS.OUT_OF_STOCK
    } else if (currentStock <= threshold) {
      return STOCK_STATUS.LOW_STOCK
    } else if (currentStock > threshold * 3) { // Arbitrary over-stock threshold
      return STOCK_STATUS.OVER_STOCK
    } else {
      return STOCK_STATUS.NORMAL
    }
  }
  
  // Get status badge classes
  static getStatusBadgeClasses(status) {
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STOCK_STATUS_COLORS[status] || STOCK_STATUS_COLORS[STOCK_STATUS.NORMAL]}`
  }
  
  // Get status label
  static getStatusLabel(status) {
    return STOCK_STATUS_LABELS[status] || 'Unknown'
  }
  
  // Format inventory display
  static formatDisplay(inventory, rawMaterial = null, branch = null) {
    const materialName = rawMaterial?.name || inventory.rawMaterial?.name || 'Unknown Material'
    const branchName = branch?.name || inventory.branch?.name || 'Unknown Branch'
    return `${materialName} at ${branchName}`
  }
  
  // Format stock display with unit
  static formatStock(currentStock, unit = null) {
    const unitText = unit?.abbreviation || unit?.name || ''
    return `${currentStock} ${unitText}`.trim()
  }
  
  // Calculate stock value
  static calculateStockValue(currentStock, unitPrice) {
    return (currentStock || 0) * (unitPrice || 0)
  }
  
  // Search and filter methods
  static search(inventories, searchTerm) {
    if (!searchTerm) return inventories
    
    const term = searchTerm.toLowerCase()
    return inventories.filter(inventory => {
      const materialName = inventory.rawMaterial?.name || ''
      const materialCode = inventory.rawMaterial?.code || ''
      const branchName = inventory.branch?.name || ''
      
      return materialName.toLowerCase().includes(term) ||
             materialCode.toLowerCase().includes(term) ||
             branchName.toLowerCase().includes(term)
    })
  }
  
  static filterByStatus(inventories, status) {
    if (!status) return inventories
    
    return inventories.filter(inventory => {
      const threshold = inventory.rawMaterial?.threshold || 0
      const currentStatus = this.getStockStatus(inventory.currentStock, threshold)
      return currentStatus === status
    })
  }
  
  static filterByBranch(inventories, branchId) {
    if (!branchId) return inventories
    
    return inventories.filter(inventory => {
      const inventoryBranchId = inventory.branch?._id || inventory.branch
      return inventoryBranchId === branchId
    })
  }
  
  static filterByMaterial(inventories, materialId) {
    if (!materialId) return inventories
    
    return inventories.filter(inventory => {
      const inventoryMaterialId = inventory.rawMaterial?._id || inventory.rawMaterial
      return inventoryMaterialId === materialId
    })
  }
  
  // Sort inventories
  static sort(inventories, sortBy = 'rawMaterial.name', sortOrder = 'asc') {
    return [...inventories].sort((a, b) => {
      let valueA, valueB
      
      // Handle nested properties
      if (sortBy.includes('.')) {
        const keys = sortBy.split('.')
        valueA = keys.reduce((obj, key) => obj?.[key], a)
        valueB = keys.reduce((obj, key) => obj?.[key], b)
      } else {
        valueA = a[sortBy]
        valueB = b[sortBy]
      }
      
      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB?.toLowerCase() || ''
      }
      
      // Handle null/undefined values
      if (valueA == null) valueA = ''
      if (valueB == null) valueB = ''
      
      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }
  
  // Analytics methods
  static getSummary(inventories) {
    const summary = {
      total: inventories.length,
      active: inventories.filter(inv => inv.isActive).length,
      inactive: inventories.filter(inv => !inv.isActive).length,
      stockStatus: {
        normal: 0,
        lowStock: 0,
        outOfStock: 0,
        overStock: 0
      },
      totalValue: 0,
      averageStock: 0
    }
    
    let totalStock = 0
    
    inventories.forEach(inventory => {
      const threshold = inventory.rawMaterial?.threshold || 0
      const status = this.getStockStatus(inventory.currentStock, threshold)
      summary.stockStatus[status.replace('_', '')]++
      
      const value = this.calculateStockValue(inventory.currentStock, inventory.unitPrice)
      summary.totalValue += value
      
      totalStock += inventory.currentStock || 0
    })
    
    if (inventories.length > 0) {
      summary.averageStock = Math.round(totalStock / inventories.length)
    }
    
    return summary
  }
  
  // Get low stock alerts
  static getLowStockAlerts(inventories) {
    return inventories.filter(inventory => {
      const threshold = inventory.rawMaterial?.threshold || 0
      const status = this.getStockStatus(inventory.currentStock, threshold)
      return status === STOCK_STATUS.LOW_STOCK || status === STOCK_STATUS.OUT_OF_STOCK
    })
  }
  
  // Create default inventory object
  static createDefault() {
    return this.create()
  }
  
  // Prepare data for API requests
  static prepareForCreate(inventoryData) {
    return {
      branch: inventoryData.branch,
      rawMaterial: inventoryData.rawMaterial,
      currentStock: Number(inventoryData.currentStock || 0),
      unitPrice: inventoryData.unitPrice ? Number(inventoryData.unitPrice) : undefined,
      isActive: inventoryData.isActive !== false
    }
  }
  
  static prepareForUpdate(inventoryData) {
    const updateData = {}
    
    if (inventoryData.currentStock !== undefined) {
      updateData.currentStock = Number(inventoryData.currentStock)
    }
    
    if (inventoryData.unitPrice !== undefined) {
      updateData.unitPrice = Number(inventoryData.unitPrice)
    }
    
    if (inventoryData.isActive !== undefined) {
      updateData.isActive = inventoryData.isActive
    }
    
    return updateData
  }
  
  // Prepare data for bulk assignment
  static prepareBulkAssignment(branchId, rawMaterialIds) {
    return {
      branch: branchId,
      rawMaterials: rawMaterialIds.map(id => ({
        rawMaterial: id
      }))
    }
  }
}

export default RawMaterialInventoryModel